import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "features/config/hooks";
import {
  Divider,
  Button,
  Fade,
  Paper,
  Popper,
  PopperPlacementType,
  ButtonGroup,
} from "@mui/material";
import {
  FiSettings,
  FiShoppingCart,
  FiBarChart,
  FiUser,
  FiChevronDown,
  FiMenu,
  FiX,
  FiTrendingUp,
  FiShield,
  FiUsers,
} from "react-icons/fi";
import { IoMdArrowDropdown } from "react-icons/io";
import {
  selectGetAdminSession,
  getAdminSession,
  GetAdminSessionState,
} from "../slices/get-admin-session.slice";
import {
  LogoutAdminState,
  logoutAdmin,
  resetLogoutAdmin,
  selectLogoutAdmin,
} from "../slices/logout-admin.slice";
import { AdminChangePasswordModal } from "../modals";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";
import { FaStore } from "react-icons/fa";

interface LandingPageModuleNavigationProps {
  url: string;
  assetPath: string;
  label: string;
  onNavigationClick: () => void;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  features: string[];
  color: string;
}

interface Nav {
  [key: string]: boolean;
}

export function AdminLandingPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const getAdminSessionState = useAppSelector(selectGetAdminSession);
  const getLogoutAdminState = useAppSelector(selectLogoutAdmin);

  const [openChangePasswordModal, setOpenChangePasswordModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<PopperPlacementType>();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleClick =
    (newPlacement: PopperPlacementType) =>
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      setOpen((prev) => placement !== newPlacement || !prev);
      setPlacement(newPlacement);
    };

  const [navAvailability, setNavAvailability] = useState<Nav>({
    shop: false,
    sos: false,
    sosSell: false,
    sales: false,
    ms6: true,
    svr: false,
  });

  const administrationUrl = () => {
    const admin = getAdminSessionState.data?.admin;
    if (admin?.is_marketing_admin) {
      return "/admin/catering";
    } else {
      return "/admin/dashboard/customer-feedback";
    }
  };

  let nav = [
    {
      url: administrationUrl(),
      label: "Administration",
      availability: navAvailability.shop,
      assetPath: "admin.png",
      icon: FiSettings,
      description: "Complete system configuration and user management",
      features: [
        "User Management",
        "Store Management",
        "Report Generation",
        "and more",
      ],
      color: "bg-blue-500",
    },
    {
      url: "/admin/material/order",
      label: "Stock Ordering",
      availability: navAvailability.sos,
      assetPath: "stock-order.png",
      icon: FiShoppingCart,
      description: "Streamlined inventory ordering and supplier management",
      features: ["Stock order placement", "Tei-Side Order Management"],
      color: "bg-green-500",
    },
    {
      url: "/admin/material/selling",
      label: "Stock Selling",
      availability: navAvailability.sosSell,
      assetPath: "stock-sell.png",
      icon: FaStore,
      description: "Point of sale and retail management system",
      features: [
        "Supplier Order Management",
        "Supplier to Supplier Ordering",
        "Stock Pricing Management",
      ],
      color: "bg-purple-500",
    },
    {
      url: "/admin/sales/dashboard",
      label: "Sales",
      availability: false,
      assetPath: "admin.png",
      icon: FiTrendingUp,
      description: "Sales performance and analytics dashboard",
      features: ["Sales Reports", "Performance Metrics", "Team Analytics"],
      color: "bg-orange-500",
    },
    {
      url: "/admin/ms6/dashboard-page",
      label: "6MS",
      availability: navAvailability.ms6,
      assetPath: "6m.png",
      icon: FiBarChart,
      description: "Comprehensive business intelligence and reporting",
      features: [
        "Real-time Reports",
        "Performance Metrics",
        "Business Intelligence",
      ],
      color: "bg-orange-500",
    },
    {
      url: "/admin/svr/dashboard",
      label: "SVR",
      availability: false,
      assetPath: "admin.png",
      icon: FiShield,
      description: "Security and verification reporting",
      features: ["Security Reports", "Verification Status", "Compliance"],
      color: "bg-red-500",
    },
  ];

  const { first_name, last_name } =
    getAdminSessionState.data?.admin.user_details ?? {};

  const payableEnable =
    getAdminSessionState.data?.admin.user_details.sos_groups.some(
      (group) => group.id === 7 || group.id === 8 || group.id === 9
    );

  const noStockOrderingPermission =
    getAdminSessionState.data?.admin.user_details.sos_groups.some(
      (group) =>
        group.id === 0 ||
        group.id === 1 ||
        group.id === 2 ||
        group.id === 3 ||
        group.id === 4 ||
        group.id === 5 ||
        group.id === 6 ||
        group.id === 10 ||
        group.id === 11
    );

  const hideShopIfSvrIsEnabled =
    getAdminSessionState.data?.admin.user_details.groups.some(
      (group) => group.id != 17
    );

  useEffect(() => {
    if (getAdminSessionState.data) {
      const userAdmin = getAdminSessionState.data?.admin?.user_details;

      setNavAvailability((prevState) => ({
        ...prevState,
        shop:
          userAdmin?.groups?.length !== 0 && Boolean(hideShopIfSvrIsEnabled),
        sos:
          userAdmin?.sos_groups?.length !== 0 &&
          Boolean(noStockOrderingPermission),
        sosSell: userAdmin?.sos_groups_seller?.length !== 0,
        sales: false,
        ms6: userAdmin?.groups?.length !== 0,
        svr: userAdmin?.groups.some(
          (group) => group.id === 1 || group.id === 17
        ),
      }));
    }
  }, [getAdminSessionState.data]);

  useEffect(() => {
    if (getLogoutAdminState.status === LogoutAdminState.success) {
      dispatch(getAdminSession());
      dispatch(resetLogoutAdmin());
      navigate("/admin");
    }
  }, [getLogoutAdminState, dispatch, navigate]);

  const handleOnNavigationClick = () => {};

  const stats = [
    { label: "Active Stores", value: "150+", icon: FaStore },
    { label: "Monthly Orders", value: "50K+", icon: FiShoppingCart },
    { label: "System Users", value: "2K+", icon: FiUsers },
    { label: "Uptime", value: "99.9%", icon: FiTrendingUp },
  ];

  return (
    <>
      {GetAdminSessionState.success === getAdminSessionState.status && (
        <div className="min-h-screen bg-gray-50">
          {/* Header */}
          <header className="bg-white shadow-sm border-b">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex justify-between items-center h-16">
                <div className="flex items-center">
                  <div className="flex-shrink-0 flex items-center">
                    <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-xl">P</span>
                    </div>
                    <div className="ml-3">
                      <h1 className="text-xl font-bold text-gray-900">
                        PopCore
                      </h1>
                      <p className="text-sm text-gray-500">Webwork Ecosystem</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <button
                      onClick={handleClick("bottom-end")}
                      className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                    >
                      <FiUser className="w-4 h-4" />
                      <span className="hidden md:inline">
                        {first_name + " " + last_name}
                      </span>
                      <IoMdArrowDropdown size={20} />
                    </button>

                    <Popper
                      open={open}
                      anchorEl={anchorEl}
                      placement={placement}
                      transition
                    >
                      {({ TransitionProps }) => (
                        <Fade {...TransitionProps} timeout={350}>
                          <Paper variant="outlined" className="mt-2">
                            <ButtonGroup orientation="vertical">
                              <Button
                                variant="text"
                                onClick={() => setOpenChangePasswordModal(true)}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100"
                              >
                                Change Password
                              </Button>
                              <Button
                                variant="text"
                                onClick={() => dispatch(logoutAdmin())}
                                className="px-4 py-2 text-gray-700 hover:bg-gray-100"
                              >
                                Logout
                              </Button>
                            </ButtonGroup>
                          </Paper>
                        </Fade>
                      )}
                    </Popper>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* Hero Section */}
          <section className="bg-gradient-to-br from-red-50 to-gray-100 py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                  PopCore
                  <span className="block text-red-600">Webwork Ecosystem</span>
                </h1>
                <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                  Smart operations built for fast-moving, flavor-driven
                  businesses.
                </p>
              </div>
            </div>
          </section>

          {/* Modules Section */}
          <section id="systems" className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Manage What Matters
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Your available modules are ready — let’s get started.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {nav
                  .filter((module) => module.availability === true)
                  .map((module, index) => (
                    <LandingPageModuleNavigation
                      key={module.url}
                      url={module.url}
                      assetPath={module.assetPath ?? ""}
                      label={module.label}
                      onNavigationClick={handleOnNavigationClick}
                      icon={module.icon}
                      description={module.description}
                      features={module.features}
                      color={module.color}
                    />
                  ))}
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="bg-gray-800 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                {/* <div className="flex items-center justify-center mb-4">
                  <span className="ml-2 text-white font-bold">
                    Taters Group
                  </span>
                </div>
                <p className="text-gray-400 mb-4">
                  Comprehensive business management platform for modern
                  enterprises.
                </p> */}
                <p className="text-gray-400">
                  © 2025 Taters Group. All rights reserved.
                </p>
              </div>
            </div>
          </footer>
        </div>
      )}

      <AdminChangePasswordModal
        open={openChangePasswordModal}
        onClose={() => setOpenChangePasswordModal(false)}
      />
    </>
  );
}

function LandingPageModuleNavigation(props: LandingPageModuleNavigationProps) {
  const {
    url,
    assetPath,
    label,
    onNavigationClick,
    icon: Icon,
    description,
    features,
    color,
  } = props;

  return (
    <Link
      to={url}
      onClick={onNavigationClick}
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group block h-full"
    >
      <div className="flex flex-col h-full p-6">
        {/* Icon and Title */}
        <div className="flex items-center mb-4">
          <div className={`${color} p-3 rounded-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-bold text-gray-900">{label}</h3>
            <p className="text-gray-600 text-sm">{description}</p>
          </div>
        </div>

        {/* Features */}
        <div className="space-y-2 mb-4">
          {features.map((feature, idx) => (
            <div key={idx} className="flex items-center text-gray-700 text-sm">
              <div className="w-2 h-2 bg-red-600 rounded-full mr-3"></div>
              <span>{feature}</span>
            </div>
          ))}
        </div>

        {/* Spacer to push footer down */}
        <div className="mt-auto flex items-center justify-between">
          <img
            className="w-12 h-12 object-contain opacity-75"
            src={`${REACT_APP_DOMAIN_URL}/api/assets/images/shared/landing_icon/${assetPath}`}
            alt={`Navigation image: ${assetPath}`}
          />
          <div className="bg-gray-900 text-white px-4 py-2 rounded-lg group-hover:bg-red-600 transition-colors text-sm">
            Access System
          </div>
        </div>
      </div>
    </Link>
  );
}
